import { useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import SlittingPlanner from "@/components/SlittingPlanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQueryClient } from "@tanstack/react-query";
import { checkCoil, slitMotherCoil, slitLabelsPdfUrl, slitLabelsTsplUrl, printTsplViaBridge, bridgeDownloadUrl } from "@/lib/api";
import ReconcileDialog from "@/components/ReconcileDialog";
import {
  Power, CheckCircle2, Search, Scissors, AlertTriangle,
} from "lucide-react";

const Consume = () => {
  const [scannerActive, setScannerActive] = useState(true);
  const [barcode, setBarcode] = useState("");
  const [mother, setMother] = useState(null);
  const [status, setStatus] = useState(""); // 'ready' | 'pending' | 'consumed' | 'not_found' | 'sfg'
  const [manualLookup, setManualLookup] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [reconcileBatch, setReconcileBatch] = useState(null);
  const lastScanRef = useRef({ value: "", time: 0 });
  const queryClient = useQueryClient();

  const beep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = 660;
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
      osc.start();
      osc.stop(ctx.currentTime + 0.22);
    } catch (err) { if (process.env.NODE_ENV !== "production") console.debug("beep unavailable", err); }
  };

  const lookup = useCallback(async (text) => {
    setBarcode(text);
    setMother(null);
    try {
      const res = await checkCoil(text);
      if (!res?.exists) {
        setStatus("not_found");
        toast.error(`QR code ${text} not in inventory`);
        return;
      }
      const c = res.coil;
      if (c.kind === "sfg") {
        setStatus("sfg");
        toast.warning(`${text} is an SFG — only RM can be slit`);
        setMother(c);
        return;
      }
      setMother(c);
      if (c.status === "consumed") {
        setStatus("consumed");
        toast.warning(`RM ${text} already slit/consumed`);
      } else if (c.status === "pending") {
        setStatus("pending");
        toast.error(`${text} is pending — receive it at the gate first`);
      } else {
        setStatus("ready");
        toast.success(`RM ${text} ready for slitting`);
      }
    } catch (err) {
      console.error("Coil lookup failed", err);
      toast.error("Lookup failed");
    }
  }, []);

  const handleDetected = useCallback(async (text) => {
    const now = Date.now();
    if (lastScanRef.current.value === text && now - lastScanRef.current.time < 1500) return;
    lastScanRef.current = { value: text, time: now };
    setScannerActive(false);
    beep();
    await lookup(text);
  }, [lookup]);

  const handleManualLookup = async (e) => {
    e.preventDefault();
    if (!manualLookup.trim()) return;
    await lookup(manualLookup.trim());
    setManualLookup("");
  };

  const handleSubmitPlan = async (payload) => {
    if (!mother) return;
    setSubmitting(true);
    try {
      const res = await slitMotherCoil(mother.coil_id, payload);
      toast.success(`Generated ${res.sfgs.length} SFGs from ${mother.coil_id}`);
      // Soft warning when some SFGs didn't find a matching active SFG SKU master.
      // Dispatch scans would mark these as "unmatched". We surface the top combos
      // so the Director/Slitting op knows what to add under Masters > SFG SKU.
      const unmatched = res.unmatched_sfgs || [];
      if (unmatched.length) {
        const combos = Array.from(new Set(
          unmatched.map((u) => `${u.grade} · ${u.slit_width_mm}mm · ${u.thickness_mm}mm`)
        )).slice(0, 3);
        toast.warning(
          `${unmatched.length} SFG(s) have no matching SFG SKU master — add: ${combos.join("; ")}`,
          { duration: 10000 },
        );
      }
      // One-click thermal print if the bridge is up, else fall back to PDF.
      try {
        await printTsplViaBridge(slitLabelsTsplUrl(res.batch_id));
        toast.success(`Printed ${res.sfgs.length} SFG labels`);
      } catch {
        window.open(slitLabelsPdfUrl(res.batch_id), "_blank");
        toast.info("Print Bridge not detected — opened PDF instead.", {
          action: { label: "Setup kit", onClick: () => window.open(bridgeDownloadUrl(), "_blank") },
        });
      }
      // Prompt the operator to reconcile yield (optional — they can dismiss)
      setReconcileBatch({
        id: res.batch_id,
        mother_coil_id: res.mother_coil_id,
        mother_weight_kg: res.mother_weight_kg,
        total_slit_weight_kg: res.total_slit_weight_kg,
        theoretical_yield_pct: res.mother_weight_kg ? +((res.total_slit_weight_kg / res.mother_weight_kg) * 100).toFixed(2) : 0,
      });
      setMother(null); setBarcode(""); setStatus(""); setScannerActive(true);
      queryClient.invalidateQueries({ queryKey: ["stats"] });
      queryClient.invalidateQueries({ queryKey: ["coils"] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Slitting failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => { setMother(null); setStatus(""); setBarcode(""); };

  const showPlanner = status === "ready" && mother;

  return (
    <div data-testid="consume-page" className="space-y-6">
      <div className="flex items-end justify-between gap-4 border-b border-slate-200 pb-4 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold mb-1">Slitting Lab</p>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Slit &amp; Generate SFGs
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Plan the slits first — the planner enforces width and issues SFG codes. Scan the actual RM to bind it, which fills in real SFG weights and unlocks label printing.
          </p>
        </div>
        <Button variant="outline" onClick={() => setScannerActive((s) => !s)} data-testid="toggle-scanner-btn" className="rounded-sm h-10">
          <Power className="h-4 w-4 mr-2" /> {scannerActive ? "Stop camera" : "Start camera"}
        </Button>
      </div>

      <div className={`grid grid-cols-1 ${showPlanner ? "" : "lg:grid-cols-5"} gap-6`}>
        {!showPlanner && (
          <div className="lg:col-span-2 space-y-3">
            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
              <BarcodeScanner active={scannerActive} onDetected={handleDetected} />
            </div>
            <form onSubmit={handleManualLookup} className="bg-white border border-slate-200 rounded-sm p-3 flex items-center gap-2">
              <Search className="h-4 w-4 text-slate-400 ml-1" />
              <Input
                data-testid="manual-lookup-input"
                placeholder="Type QR code and press Enter"
                value={manualLookup}
                onChange={(e) => setManualLookup(e.target.value)}
                className="border-0 shadow-none focus-visible:ring-0 h-9 font-mono-data"
              />
              <Button type="submit" data-testid="manual-lookup-btn" size="sm" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm h-9">
                Lookup
              </Button>
            </form>
            {barcode && (
              <div data-testid="last-scanned-banner" className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-sm p-3">
                <CheckCircle2 className="h-5 w-5 text-green-700" />
                <p className="font-mono-data text-sm text-slate-900 truncate" data-testid="scanned-barcode-value">{barcode}</p>
              </div>
            )}
          </div>
        )}

        <div className={showPlanner ? "" : "lg:col-span-3"}>
          {status === "" && (
            <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-500">
              Scan a RM to begin.
            </div>
          )}

          {status === "not_found" && (
            <div data-testid="not-found-panel" className="bg-white border border-red-200 rounded-sm p-6 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <p className="font-heading font-bold text-slate-900">QR code not in inventory</p>
                <p className="text-sm text-slate-600 mt-1">This QR code was never added. Check the label.</p>
              </div>
            </div>
          )}

          {(status === "consumed" || status === "pending" || status === "sfg") && mother && (
            <div data-testid="blocked-panel" className="bg-white border border-amber-200 rounded-sm p-6">
              <p className="font-heading text-base font-bold text-slate-900">Cannot slit this coil</p>
              <p className="text-sm text-slate-700 mt-1">
                {status === "consumed"
                  ? `RM ${mother.coil_id} was already slit/consumed on ${(mother.consumed_at || "").slice(0, 10) || "an earlier date"}.`
                  : status === "sfg"
                    ? `${mother.coil_id} is an SFG (semi-finished good) — it was already produced from RM ${mother.mother_coil_id || "—"}. Only RM can be slit.`
                    : `RM ${mother.coil_id} is still pending. The gate operator must receive it first.`}
              </p>
            </div>
          )}

          {showPlanner && (
            <div data-testid="planner-card" className="bg-white border border-slate-200 rounded-sm p-5 sm:p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 bg-orange-100 text-orange-800 rounded-sm flex items-center justify-center">
                  <Scissors className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-[10px] tracking-[0.25em] uppercase text-orange-700 font-semibold">Slitting plan</p>
                  <h3 className="font-heading text-lg font-bold text-slate-900 mt-0.5 font-mono-data">{mother.coil_id}</h3>
                </div>
              </div>
              <SlittingPlanner
                mother={mother}
                onCancel={handleCancel}
                onSubmit={handleSubmitPlan}
                submitting={submitting}
              />
            </div>
          )}
        </div>
      </div>

      <ReconcileDialog
        open={!!reconcileBatch}
        onOpenChange={(o) => !o && setReconcileBatch(null)}
        batch={reconcileBatch}
      />
    </div>
  );
};

export default Consume;
